import {
  Box,
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Select,
  Spinner,
  Tag,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  BranchCurrencyTarget,
  CopyToBranchesRequest,
  Offer,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  exchangeRateCalculation,
  marginBy,
  round,
} from '@texas/utils/helpers/numberHelpers';
import { useCurrencies } from '@texas/hooks/metadata/useCurrencies';
import { evaluateProfit, profitTextColor } from '../sales/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  sourceOfferId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onCopied: () => void;
}

export function CopyOfferDrawer({
  sourceOfferId,
  isOpen,
  onClose,
  onCopied,
}: Props) {
  const { t } = useTranslation();

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('offer.copyTo')}</DrawerHeader>
        {isOpen && sourceOfferId && (
          <CopyOfferDrawerInner
            sourceOfferId={sourceOfferId}
            onClose={onClose}
            onCopied={onCopied}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}

function CopyOfferDrawerInner({
  sourceOfferId,
  onClose,
  onCopied,
}: {
  sourceOfferId: number;
  onClose: () => void;
  onCopied: () => void;
}) {
  const { t } = useTranslation();
  const isDark = useColorModeValue(false, true);
  const [selectedTargets, setSelectedTargets] = useState<
    BranchCurrencyTarget[]
  >([]);
  const [pendingCurrencyByBranch, setPendingCurrencyByBranch] = useState<
    Record<number, string>
  >({});

  const {
    data: sourceOffer,
    loading: sourceLoading,
    error: sourceError,
    refetch: refetchSourceOffer,
  } = useApiResource(offerApi.getOffer);
  const {
    data: group,
    loading: groupLoading,
    error: groupError,
    refetch: refetchGroup,
  } = useApiResource(offerApi.getGroup);

  const {
    data: allCurrencies,
    loading: currenciesLoading,
    refetch: refetchCurrencies,
  } = useApiResource(
    currenciesApi.getAllCurrenciesMergedWithCustomerCurrencies,
  );
  const { convertCurrencyValue } = useCurrencies({ currencies: allCurrencies });

  const { request: copyRequest, loading: copying } = useApiRequest(
    offerApi.copyToBranches,
  );

  useEffect(() => {
    setSelectedTargets([]);
    setPendingCurrencyByBranch({});
    refetchSourceOffer(sourceOfferId);
  }, [sourceOfferId, refetchSourceOffer]);

  useEffect(() => {
    if (sourceOffer?.offerGroupId != null) {
      refetchGroup(sourceOffer.offerGroupId);
    }
  }, [sourceOffer?.offerGroupId, refetchGroup]);

  useEffect(() => {
    if (group) {
      refetchCurrencies(group.customerId);
    }
  }, [group, refetchCurrencies]);

  const toggleTarget = (branchId: number, currencyId: number) => {
    setSelectedTargets((prev) => {
      const exists = prev.some(
        (t) => t.branchId === branchId && t.currencyId === currencyId,
      );
      return exists
        ? prev.filter(
            (t) => !(t.branchId === branchId && t.currencyId === currencyId),
          )
        : [...prev, { branchId, currencyId }];
    });
  };

  const handleCopy = async () => {
    const req: CopyToBranchesRequest = { targets: selectedTargets };
    await copyRequest(sourceOfferId, req);
    onCopied();
    onClose();
  };

  const addPendingCurrency = (branchId: number, currencyId: number) => {
    setSelectedTargets((prev) =>
      prev.some((t) => t.branchId === branchId && t.currencyId === currencyId)
        ? prev
        : [...prev, { branchId, currencyId }],
    );
    setPendingCurrencyByBranch((prev) => ({ ...prev, [branchId]: '' }));
  };

  const sourceBranch = group?.branches.find(
    (branch) => branch.branchId === sourceOffer?.branchId,
  );
  const sourceCurrency = sourceBranch?.currencies.find(
    (currency) => currency.currencyId === sourceOffer?.currencyId,
  );
  const sourceOffers = sourceCurrency
    ? sourceCurrency.childOffers.length > 0
      ? sourceCurrency.childOffers
      : sourceCurrency.masterOffer
      ? [sourceCurrency.masterOffer]
      : []
    : [];
  const sourceCurrencyMeta = sourceOffer
    ? (allCurrencies ?? []).find(
        (currency) => currency.id === sourceOffer.currencyId,
      )
    : null;

  const rowLabel = (offer: Offer) => {
    if (!offer.matrixNodeValueXName && !offer.matrixNodeValueYName) {
      return t('offer.offer');
    }
    if (offer.matrixNodeValueXName && offer.matrixNodeValueYName) {
      return `${offer.matrixNodeValueXName} / ${offer.matrixNodeValueYName}`;
    }
    return offer.matrixNodeValueXName ?? offer.matrixNodeValueYName ?? '—';
  };

  const sourceRows = sourceOffers.flatMap((offer) =>
    offer.tierPrices.map((tierPrice) => ({
      key: `${offer.id}-${tierPrice.id}`,
      offer,
      label: rowLabel(offer),
      quantity: tierPrice.quantity,
      price: tierPrice.price,
    })),
  );

  const previewRows = useMemo(
    () =>
      selectedTargets.flatMap((target) => {
        if (!group) return [];

        const branch = group.branches.find(
          (b) => b.branchId === target.branchId,
        );
        const targetCurrency = (allCurrencies ?? []).find(
          (currency) =>
            currency.branchId === target.branchId &&
            currency.id === target.currencyId,
        );
        if (!branch || !targetCurrency || !sourceOffer) return [];

        return sourceRows.map((row) => {
          const price =
            row.price != null
              ? round(
                  convertCurrencyValue(
                    sourceOffer.currencyId,
                    target.currencyId,
                    row.price,
                    'sell',
                  ),
                  5,
                )
              : null;
          const matchedInquiry = branch.acceptedInquiries.find(
            (inq) => inq.isMatched,
          );
          const matchedTier = matchedInquiry?.tierPrices.find(
            (tierPrice) => tierPrice.quantity === row.quantity,
          );
          const inquiryPrice =
            matchedInquiry && matchedTier?.price != null
              ? exchangeRateCalculation(
                  matchedTier.price,
                  matchedInquiry.exchangeRate,
                  targetCurrency.sellingRate,
                  5,
                )
              : null;
          const margin =
            price != null && inquiryPrice != null
              ? marginBy(price, inquiryPrice, false)
              : null;

          return {
            ...row,
            branch,
            targetCurrency,
            price,
            margin,
            profit:
              price != null && inquiryPrice != null
                ? evaluateProfit(price, inquiryPrice)
                : null,
          };
        });
      }),
    [
      allCurrencies,
      convertCurrencyValue,
      group,
      selectedTargets,
      sourceOffer,
      sourceRows,
    ],
  );

  const loading = sourceLoading || groupLoading || currenciesLoading;
  const error = sourceError ?? groupError;

  return (
    <DrawerBody>
      {error && <Text color="red.300">{error.message}</Text>}
      {loading ? (
        <Spinner size="sm" />
      ) : (
        group && (
          <>
            <Flex direction="column" gap={4}>
              {sourceOffer && sourceBranch && sourceCurrency && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    {t('offer.copyingFrom')}
                  </Text>
                  <Flex gap={2} wrap="wrap" mb={2}>
                    <Tag>{sourceBranch.branchIdentifier}</Tag>
                    <Tag colorScheme="blue">
                      {sourceCurrencyMeta?.code ?? sourceOffer.currencyId}
                    </Tag>
                    <Tag variant="subtle">
                      {t('inquiry.exchangeRate')}: {sourceOffer.exchangeRate}
                    </Tag>
                  </Flex>
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('offer.offer')}</Th>
                          <Th>{t('general.quantity')}</Th>
                          <Th>{t('inquiry.price')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {sourceRows.map((row) => (
                          <Tr key={row.key}>
                            <Td>{row.label}</Td>
                            <Td>{row.quantity}</Td>
                            <Td>
                              {row.price != null
                                ? `${row.price} ${
                                    sourceCurrencyMeta?.code ?? ''
                                  }`
                                : '—'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {group.branches.map((branch) => {
                const branchCurrencies = (allCurrencies ?? []).filter(
                  (c) => c.branchId === branch.branchId,
                );
                const alreadyAdded = branch.currencies.map((c) => c.currencyId);
                const addedCurrencies = branchCurrencies.filter((currency) =>
                  alreadyAdded.includes(currency.id),
                );
                const notAddedCurrencies = branchCurrencies.filter(
                  (currency) =>
                    !alreadyAdded.includes(currency.id) &&
                    !selectedTargets.some(
                      (target) =>
                        target.branchId === branch.branchId &&
                        target.currencyId === currency.id,
                    ),
                );
                const selectedNewCurrencies = selectedTargets
                  .filter(
                    (target) =>
                      target.branchId === branch.branchId &&
                      !alreadyAdded.includes(target.currencyId),
                  )
                  .map((target) =>
                    branchCurrencies.find(
                      (currency) => currency.id === target.currencyId,
                    ),
                  )
                  .filter(Boolean);

                return (
                  <Box key={branch.branchId}>
                    <Text fontWeight="semibold" mb={2}>
                      {branch.branchName}
                    </Text>
                    <Flex direction="column" gap={2}>
                      {addedCurrencies.map((currency) => {
                        const isSelected = selectedTargets.some(
                          (t) =>
                            t.branchId === branch.branchId &&
                            t.currencyId === currency.id,
                        );
                        return (
                          <Button
                            key={currency.id}
                            size="sm"
                            justifyContent="space-between"
                            variant={isSelected ? 'texas-solid' : 'texas-light'}
                            onClick={() =>
                              toggleTarget(branch.branchId, currency.id)
                            }
                          >
                            <Flex align="center" gap={2}>
                              <Tag size="sm" colorScheme="blue">
                                {currency.code}
                              </Tag>
                              <Text fontSize="sm">{currency.name}</Text>
                            </Flex>
                            <Badge colorScheme={isSelected ? 'orange' : 'gray'}>
                              {isSelected
                                ? t('offer.replacePrices')
                                : t('offer.exists')}
                            </Badge>
                          </Button>
                        );
                      })}

                      {selectedNewCurrencies.map((currency) => (
                        <Button
                          key={currency!.id}
                          size="sm"
                          justifyContent="space-between"
                          variant="texas-solid"
                          onClick={() =>
                            toggleTarget(branch.branchId, currency!.id)
                          }
                        >
                          <Flex align="center" gap={2}>
                            <Tag size="sm" colorScheme="blue">
                              {currency!.code}
                            </Tag>
                            <Text fontSize="sm">{currency!.name}</Text>
                          </Flex>
                          <Badge colorScheme="green">
                            {t('offer.willBeAdded')}
                          </Badge>
                        </Button>
                      ))}

                      {notAddedCurrencies.length > 0 && (
                        <Select
                          size="sm"
                          placeholder={t('offer.addCurrency')}
                          value={pendingCurrencyByBranch[branch.branchId] || ''}
                          onChange={(e) => {
                            const currencyId = Number(e.target.value);
                            if (currencyId) {
                              addPendingCurrency(branch.branchId, currencyId);
                            }
                          }}
                        >
                          {notAddedCurrencies.map((currency) => (
                            <option key={currency.id} value={currency.id}>
                              {currency.code} · {currency.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Flex>
                  </Box>
                );
              })}

              {previewRows.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    {t('general.preview')}
                  </Text>
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('general.branch')}</Th>
                          <Th>{t('general.currency')}</Th>
                          <Th>{t('offer.offer')}</Th>
                          <Th>{t('general.quantity')}</Th>
                          <Th>{t('inquiry.price')}</Th>
                          <Th>{t('variant.sales.margin')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {previewRows.map((row) => (
                          <Tr
                            key={`${row.branch.branchId}-${row.targetCurrency.id}-${row.key}`}
                          >
                            <Td>{row.branch.branchIdentifier}</Td>
                            <Td>{row.targetCurrency.code}</Td>
                            <Td>{row.label}</Td>
                            <Td>{row.quantity}</Td>
                            <Td>
                              {row.price != null
                                ? `${row.price} ${row.targetCurrency.code}`
                                : '—'}
                            </Td>
                            <Td
                              color={
                                row.profit == null
                                  ? 'gray.500'
                                  : profitTextColor(row.profit, isDark)
                              }
                            >
                              {row.margin != null
                                ? `${row.margin.toFixed(1)}%`
                                : '—'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Flex>
            <Button
              mt={4}
              variant="texas-solid"
              size="sm"
              isLoading={copying}
              isDisabled={selectedTargets.length === 0}
              onClick={handleCopy}
            >
              {t('offer.copyAndConvert')}
            </Button>
          </>
        )
      )}
    </DrawerBody>
  );
}
